import React, { CSSProperties, forwardRef } from 'react';
import type { ChipProps } from '@mui/material';
import { Chip as ChipMUI } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import { CancelRounded, EditRounded } from '@mui/icons-material';

import { SHAPE_CIRCULAR, SHAPE_ROUNDED, SHAPE_SQUARE } from '@/mui/constants';

import { omitUndefinedProps } from '@/mui/helpers';

import IconButton from '../IconButton/IconButton';
import Stack from '../Stack/Stack';
import {
  CUSTOM_ATTRIBUTE_FULL_WIDTH,
  CUSTOM_ATTRIBUTE_OVERLAY_EDIT,
  CUSTOM_ATTRIBUTE_SHAPE,
  CUSTOM_ATTRIBUTE_SINGLE_COLOR,
} from './Chip.api';

import styles from './Chip.module.scss';

type Props = {
  fullWidth?: boolean;
  shape?: typeof SHAPE_SQUARE | typeof SHAPE_ROUNDED | typeof SHAPE_CIRCULAR;
  onApplyIcon?: SvgIconComponent;
  onApply?: () => void;
  onCancelIcon?: SvgIconComponent;
  onCancel?: () => void;
};
const Chip = forwardRef<HTMLDivElement, ChipProps & Props>((props$, ref) => {
  const theme = useTheme();

  const { onCancel, onApply, ...props } = props$;
  const singleColor = props.color && !Object.prototype.hasOwnProperty.call(theme.palette, props.color);

  let overlayEdit = false;
  if (onApply || onCancel) {
    overlayEdit = true;
    props.onDelete = () => null;

    const ApplyIcon = props.onApplyIcon || EditRounded;
    const CancelIcon = props.onCancelIcon || CancelRounded;

    props.deleteIcon = (
      <Stack direction="row" alignItems="center" spacing={0}>
        {onApply && (
          <IconButton className={styles.OverlapButton} size="small" color="inherit" onClick={onApply}>
            <ApplyIcon fontSize={props.size ?? 'medium'} />
          </IconButton>
        )}
        {onCancel && (
          <IconButton className={styles.OverlapButton} size="small" color="inherit" onClick={onCancel}>
            <CancelIcon fontSize={props.size ?? 'medium'} />
          </IconButton>
        )}
      </Stack>
    );
  }

  let { style } = props;

  if (singleColor) {
    if (!style) {
      style = {};
    }

    style.color = props.color;
  }

  return (
    <ChipMUI
      {...omitUndefinedProps({
        ...props,
        shape: undefined,
        fullWidth: undefined,
        color: singleColor ? 'primary' : props.color,
        [CUSTOM_ATTRIBUTE_SHAPE]: props.shape,
        [CUSTOM_ATTRIBUTE_FULL_WIDTH]: props.fullWidth ? '' : undefined,
        [CUSTOM_ATTRIBUTE_SINGLE_COLOR]: singleColor ? '' : undefined,
        [CUSTOM_ATTRIBUTE_OVERLAY_EDIT]: overlayEdit ? '' : undefined,
      })}
      style={style as CSSProperties}
      ref={ref}
    />
  );
});

Chip.displayName = 'Chip';

export default Chip;
